import { useT } from "@transifex/react";
import {
    NearestAdaptableRange,
    MinMaxGradeRange,
    MinMaxGradingScale,
    PercentageGradingScale,
    SettingsSection,
    SettingsSubSection,
} from "p6m-p6u";
import { useEffect, useState } from "react";
import { getStandardOrDefaultConfiguration } from "../../../../../../helpers/PDFHelper";
import {
    convertToMinMaxGradingScale,
    createPercentageFromMinMax,
    findAllAdaptableRangeTargets,
    updateGradeRange,
} from "../../../../../../helpers/GradingScaleHelper";
import { ChangeSettingsParams } from "../../../PDFConfiguration";
import PDFGradeRangeEditor from "./PDFGradeRangeEditor";
import { GradeRangeLimitType, GradingScaleFormat } from "../../../../../../enums/settings";
import { EditScoreDirection } from "../../../../../../enums/directions";
import SmallTextToggle from "../../../../../../basic/smallTextToggle/SmallTextToggle";

import {
    FormatWrapper,
    GradingScaleContent,
    GradingScaleEditor,
    GradingScaleEditorCell,
    GradingScaleEditorRow,
    GradingScaleExpandable,
    GradingScaleExpandableHeader,
    ResetButton,
    ResetButtonText,
    StyledP6Icon,
    Text,
} from "./StyledComponents";

interface PDFGradingScaleEditorProps {
    gradingScaleSettings: PercentageGradingScale;
    gradingScaleFormat: GradingScaleFormat;
    overallScore: number;
    disabled?: boolean;
    changeSettings: <T extends SettingsSection, K extends SettingsSubSection<T>>(
        changeSettingsParams: ChangeSettingsParams<T, K>
    ) => void;
}

export type UpdateGradingScaleParams = {
    gradingScale: MinMaxGradingScale;
    index: number;
    minOrMax: GradeRangeLimitType;
    direction: EditScoreDirection;
};

const PDFGradingScaleEditor = (props: PDFGradingScaleEditorProps) => {
    const t = useT();
    const t_editGradingScale = t("Edit grading scale", { _tags: "PDFConfiguration" });
    const t_resetGradingScale = t("Reset grading scale", { _tags: "PDFConfiguration" });
    const t_percentageFormat = t("Percentages %", { _tags: "PDFConfiguration" });
    const t_scoreFormat = t("Scores", { _tags: "PDFConfiguration" });
    const t_gradingFormat = t("Format:", { _tags: "PDFConfiguration" });

    const standardOrDefaultGradingScaleSettings =
        getStandardOrDefaultConfiguration().settings.gradingAndSignatures.gradingScale.scale;
    const gradingScaleSettings = props.gradingScaleSettings ?? standardOrDefaultGradingScaleSettings;

    const standardFormat = getStandardOrDefaultConfiguration().settings.gradingAndSignatures.gradingScale.format;
    const gradingScaleFormat = props.gradingScaleFormat ?? standardFormat;
    const isPercentFormat = gradingScaleFormat === GradingScaleFormat.PERCENTAGE;

    const [gradingScale, setGradingScale] = useState<MinMaxGradingScale | undefined>(undefined);
    const [nearestAdaptableRanges, setNearestAdaptableRanges] = useState<NearestAdaptableRange[]>([]);

    const [isOpen, setIsOpen] = useState(false);
    const showExpandedContent =
        isOpen &&
        !props.disabled &&
        gradingScale &&
        gradingScale.length !== 0 &&
        gradingScale.length === nearestAdaptableRanges.length;

    useEffect(() => {
        // calculated inside useEffect to reduce the amount of times the clean up function runs
        // react-pdf does not seem to optimise this enough to move it outside

        const minMaxGradingScale = convertToMinMaxGradingScale(
            props.overallScore ?? 100,
            gradingScaleSettings,
            gradingScaleFormat
        );
        setGradingScale(minMaxGradingScale);

        const nearestAdaptableRanges = findAllAdaptableRangeTargets(minMaxGradingScale);
        setNearestAdaptableRanges(nearestAdaptableRanges);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.gradingScaleSettings, props.overallScore]);

    const toggleGradingScaleFormat = () => {
        const newFormat = isPercentFormat ? GradingScaleFormat.SCORE : GradingScaleFormat.PERCENTAGE;

        // convert immediately to avoid .5 in percent and flickering
        const minMaxGradingScale = convertToMinMaxGradingScale(
            props.overallScore ?? 100,
            gradingScaleSettings,
            newFormat
        );
        setGradingScale(minMaxGradingScale);

        props.changeSettings({
            sectionTitle: "gradingAndSignatures",
            settingTitle: "gradingScale",
            setting: {
                key: "format",
                value: newFormat,
            },
        });
    };

    const changeGradingScaleSetting = (newPercentageGradingScale: PercentageGradingScale) => {
        props.changeSettings({
            sectionTitle: "gradingAndSignatures",
            settingTitle: "gradingScale",
            setting: {
                key: "scale",
                value: newPercentageGradingScale,
            },
        });
    };

    const resetGradingScale = () => {
        const defaultGradingScale = standardOrDefaultGradingScaleSettings;
        changeGradingScaleSetting(defaultGradingScale);
    };

    const updateGradingScale = (updateGradingScaleParams: UpdateGradingScaleParams) => {
        const updateGradingScale = updateGradeRange({
            ...updateGradingScaleParams,
            nearestAdaptableRanges,
            format: gradingScaleFormat,
        });

        const newPercentageGradingScale = createPercentageFromMinMax(updateGradingScale);

        changeGradingScaleSetting(newPercentageGradingScale);
    };

    const renderExpandedContent = (gradingScale: MinMaxGradingScale) => {
        return (
            <GradingScaleContent>
                <FormatWrapper>
                    <Text>{t_gradingFormat}</Text>
                    <SmallTextToggle
                        isLeft={isPercentFormat}
                        togglePosition={() => {
                            toggleGradingScaleFormat();
                        }}
                        leftText={t_percentageFormat}
                        rightText={t_scoreFormat}
                    />
                </FormatWrapper>
                {gradingScale.map((gradeRange: MinMaxGradeRange, index: number) => {
                    return (
                        <GradingScaleEditorRow
                            key={"editor" + gradeRange.grade}
                            borderBottom={index < (gradingScale?.length ?? 0) - 1}
                        >
                            <GradingScaleEditorCell borderRight>
                                <Text>{gradeRange.grade}</Text>
                            </GradingScaleEditorCell>
                            <PDFGradeRangeEditor
                                gradingScale={gradingScale ?? []}
                                gradingScaleFormat={gradingScaleFormat}
                                gradeRange={gradeRange}
                                index={index}
                                overallScore={props.overallScore}
                                changeSettings={props.changeSettings}
                                adaptableScoreLeftIndex={nearestAdaptableRanges[index].left}
                                adaptableScoreRightIndex={nearestAdaptableRanges[index].right}
                                updateGradeRange={updateGradingScale}
                            />
                        </GradingScaleEditorRow>
                    );
                })}
            </GradingScaleContent>
        );
    };

    return (
        <GradingScaleEditor>
            <GradingScaleExpandable disabled={props.disabled}>
                <GradingScaleExpandableHeader
                    onClick={() => setIsOpen(!isOpen)}
                    onMouseDown={(event: React.MouseEvent) => {
                        event.preventDefault(); // prevents accidentally selecting text in the children
                    }}
                    isOpen={isOpen && !props.disabled}
                    disabled={props.disabled}
                >
                    <Text>{t_editGradingScale}</Text>
                    <StyledP6Icon name={isOpen && !props.disabled ? "chevron-up" : "chevron-down"} />
                </GradingScaleExpandableHeader>
                {showExpandedContent && renderExpandedContent(gradingScale)}
            </GradingScaleExpandable>
            <ResetButton
                icon={"close"}
                iconPosition={"LEFT"}
                buttonStyle={"LIGHT_BORDER"}
                disabled={props.disabled || props.gradingScaleSettings === standardOrDefaultGradingScaleSettings}
                onClick={props.disabled ? undefined : resetGradingScale}
            >
                <ResetButtonText>{t_resetGradingScale}</ResetButtonText>
            </ResetButton>
        </GradingScaleEditor>
    );
};

export default PDFGradingScaleEditor;
